import * as React from "react"
import * as ReactDOM from "react-dom"
import { cn } from "@/lib/utils"

// Lightweight shadcn-style Dialog built without external deps (Radix).
// API compatible enough for common usage: Dialog, DialogTrigger, DialogContent,
// DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose.

interface DialogContextValue {
  open: boolean
  setOpen: (v: boolean) => void
}

const DialogContext = React.createContext<DialogContextValue | null>(null)

function useDialogContext() {
  const ctx = React.useContext(DialogContext)
  if (!ctx) throw new Error("Dialog components must be used within <Dialog>")
  return ctx
}

export interface DialogProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children: React.ReactNode
}

export function Dialog({ open: openProp, onOpenChange, children }: DialogProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false)
  const isControlled = openProp !== undefined
  const open = isControlled ? !!openProp : uncontrolledOpen
  const setOpen = React.useCallback(
    (v: boolean) => {
      if (!isControlled) setUncontrolledOpen(v)
      onOpenChange?.(v)
    },
    [isControlled, onOpenChange]
  )

  const value = React.useMemo(() => ({ open, setOpen }), [open, setOpen])

  return <DialogContext.Provider value={value}>{children}</DialogContext.Provider>
}

export interface DialogTriggerProps {
  asChild?: boolean
  children: React.ReactElement
}

export function DialogTrigger({ asChild, children }: DialogTriggerProps) {
  const { setOpen } = useDialogContext()
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<any>, {
      onClick: (e: React.MouseEvent) => {
        const props = children.props as { onClick?: (e: React.MouseEvent) => void };
        if (props.onClick) {
          props.onClick(e)
        }
        setOpen(true)
      },
    })
  }
  return (
    <button onClick={() => setOpen(true)} className="inline-flex items-center">
      {children}
    </button>
  )
}

export interface DialogContentProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string
  children: React.ReactNode
}

export function DialogContent({ className, children, ...props }: DialogContentProps) {
  const { open, setOpen } = useDialogContext()
  const dialogRef = React.useRef<HTMLDivElement>(null)
  const startYRef = React.useRef<number | null>(null)
  const startXRef = React.useRef<number | null>(null)
  const canSwipeRef = React.useRef<boolean>(false)


  React.useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        dialogRef.current?.focus()
      })
    }
  }, [open])
  React.useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  if (!open) return null



  const onTouchStart = (e: React.TouchEvent) => {
    const y = e.touches[0].clientY
    const x = e.touches[0].clientX
    const top = dialogRef.current?.getBoundingClientRect().top ?? 0
    const offsetFromTop = y - top
    const scrollTop = dialogRef.current?.scrollTop ?? 0
    const nearTop = offsetFromTop <= 32
    canSwipeRef.current = nearTop && scrollTop <= 0
    if (canSwipeRef.current) {
      startYRef.current = y
      startXRef.current = x
    } else {
      startYRef.current = null
      startXRef.current = null
    }
  }
  const onTouchMove = (e: React.TouchEvent) => {
    if (!canSwipeRef.current || startYRef.current === null || startXRef.current === null) return
    const currentY = e.touches[0].clientY
    const currentX = e.touches[0].clientX
    const deltaY = currentY - startYRef.current
    const deltaX = currentX - startXRef.current
    const dominantVertical = Math.abs(deltaY) > Math.abs(deltaX) * 1.2
    const scrollTop = dialogRef.current?.scrollTop ?? 0
    if (scrollTop <= 0 && deltaY > 80 && dominantVertical) {
      setOpen(false)
      startYRef.current = null
      startXRef.current = null
      canSwipeRef.current = false
    }
  }
  const onTouchEnd = () => {
    startYRef.current = null
    startXRef.current = null
    canSwipeRef.current = false
  }

  const getFocusableElements = () => {
    const root = dialogRef.current
    if (!root) return [] as HTMLElement[]
    const selectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ')
    return Array.from(root.querySelectorAll<HTMLElement>(selectors)).filter(
      (el) => !el.hasAttribute('disabled') && el.tabIndex !== -1 && el.offsetParent !== null
    )
  }

  const content = (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.stopPropagation()
              setOpen(false)
            } else if (e.key === "Tab") {
              const focusables = getFocusableElements()
              if (focusables.length === 0) return
              const first = focusables[0]
              const last = focusables[focusables.length - 1]
              const active = document.activeElement as HTMLElement | null
              if (e.shiftKey) {
                if (!active || active === first) {
                  e.preventDefault()
                  last.focus()
                }
              } else {
                if (!active || active === last) {
                  e.preventDefault()
                  first.focus()
                }
              }
            }
          }}
          className={cn(
            "relative w-full max-w-sm sm:max-w-md lg:max-w-lg max-h-[90vh] sm:max-h-[85vh] overflow-auto rounded-lg border bg-background shadow-xl p-4 sm:p-6",
            className
          )}
          {...props}
        >
          {children}
        </div>
      </div>
    </div>
  )

  return ReactDOM.createPortal(content, document.body)
}

export function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("sticky top-0 z-10 border-b bg-background p-4 sm:p-6", className)} {...props} />
}

export function DialogTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cn("text-lg sm:text-xl font-semibold", className)} {...props} />
}

export function DialogDescription({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn("text-sm sm:text-base text-muted-foreground", className)} {...props} />
}

export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("border-t bg-background p-4 sm:p-6 flex items-center justify-end gap-2 sm:gap-3", className)} {...props} />
}

export interface DialogCloseProps {
  asChild?: boolean
  children: React.ReactElement
}

export function DialogClose({ asChild, children }: DialogCloseProps) {
  const { setOpen } = useDialogContext()
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<any>, {
      onClick: (e: React.MouseEvent) => {
        const props = children.props as { onClick?: (e: React.MouseEvent) => void };
        if (props.onClick) {
          props.onClick(e)
        }
        setOpen(false)
      },
    })
  }
  return (
    <button onClick={() => setOpen(false)} className="inline-flex items-center">
      {children}
    </button>
  )
}

